import API_BASE from "./api";


export class OfferService {

    // Get active offers (public)
    static async getActiveOffers() {
        try {
            const response = await fetch(`${API_BASE}/offers/active`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to get active offers');
            }

            return {
                success: true,
                data: data.data || [],
                count: data.count || 0,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to get active offers:', error);
            return {
                success: false,
                data: [],
                count: 0,
                error: error.message
            };
        }
    }

    // Get seasonal offers (public)
    static async getSeasonalOffers() {
        try {
            const response = await fetch(`${API_BASE}/offers/seasonal`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to get seasonal offers');
            }

            return {
                success: true,
                data: data.data || [],
                count: data.count || 0,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to get seasonal offers:', error);
            return {
                success: false,
                data: [],
                count: 0,
                error: error.message
            };
        }
    }

    // Validate offer code (works for both guest and logged-in users)
    static async validateOffer(code, cartData = null, sessionId = null) {
        try {
            // Prepare headers
            const headers = {
                'Content-Type': 'application/json'
            };

            // Add auth token if user is logged in
            const token = localStorage.getItem('auth_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Add session ID for guest users
            if (sessionId && !token) {
                headers['X-Session-ID'] = sessionId;
            }

            // Prepare request body
            const requestBody = { code };
            if (cartData) {
                requestBody.cart = cartData;
            }

            const response = await fetch(`${API_BASE}/offers/validate`, {
                method: 'POST',
                headers: headers,
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            // Return validation result even if not valid
            return {
                success: data.success,
                isValid: data.data?.isValid || false,
                discountAmount: data.data?.discountAmount || 0,
                message: data.data?.message || data.message,
                offer: data.data?.offer,
                error: !data.success ? data.message : null
            };
        } catch (error) {
            console.error('Failed to validate offer:', error);
            return {
                success: false,
                isValid: false,
                discountAmount: 0,
                message: 'Offer validation failed',
                error: error.message
            };
        }
    }


    static async applyOffer(code, sessionId = null) {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            const token = localStorage.getItem('auth_token');

            // For guest users, always use session ID
            if (sessionId) {
                headers['X-Session-ID'] = sessionId;
            }

            // For logged-in users, add auth token
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // console.log('🔄 Applying offer:', { code, hasToken: !!token, hasSession: !!sessionId });

            const response = await fetch(`${API_BASE}/offers/apply`, {
                method: 'POST',
                headers: headers,
                credentials: 'include',
                body: JSON.stringify({ code })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to apply offer');
            }

            // console.log('✅ Offer applied successfully:', data);

            return {
                success: data.success,
                cart: data.data?.cart,
                discount: data.data?.discount || 0,
                finalTotal: data.data?.finalTotal || 0,
                offerDetails: data.data?.offerDetails,
                message: data.message,
                isGuest: data.data?.isGuest || false
            };
        } catch (error) {
            console.error('Failed to apply offer:', error);
            return {
                success: false,
                cart: null,
                discount: 0,
                finalTotal: 0,
                error: error.message
            };
        }
    }

    static async removeOffer(sessionId = null) {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            const token = localStorage.getItem('auth_token');

            // For guest users, always use session ID
            if (sessionId) {
                headers['X-Session-ID'] = sessionId;
            }

            // For logged-in users, add auth token
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE}/offers/remove`, {
                method: 'DELETE',
                headers: headers,
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to remove offer');
            }

            return {
                success: data.success,
                cart: data.data,
                message: data.message
            };
        } catch (error) {
            console.error('Failed to remove offer:', error);
            return {
                success: false,
                cart: null,
                error: error.message
            };
        }
    }


    // Quick apply offer to cart (simplified for product cards)
    static async quickApplyOffer(code, sessionId = null) {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };

            const token = localStorage.getItem('auth_token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            } else if (sessionId) {
                headers['X-Session-ID'] = sessionId;
            }

            const response = await fetch(`${API_BASE}/offers/validate`, {
                method: 'POST',
                headers: headers,
                credentials: 'include',
                body: JSON.stringify({ code })
            });

            const data = await response.json();

            return {
                success: data.success,
                isValid: data.data?.isValid || false,
                message: data.data?.message || data.message,
                offer: data.data?.offer
            };
        } catch (error) {
            console.error('Quick apply offer failed:', error);
            return {
                success: false,
                isValid: false,
                message: 'Failed to apply offer'
            };
        }
    }

    // Check if offer can be applied to cart
    static async canApplyOffer(code, cartSubtotal = 0, categories = []) {
        try {
            const response = await fetch(`${API_BASE}/offers/active`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to check offers');
            }

            const offers = data.data || [];
            const targetOffer = offers.find(offer => offer.code === code);

            if (!targetOffer) {
                return {
                    canApply: false,
                    reason: 'Offer not found or inactive'
                };
            }

            // Check minimum cart value
            if (targetOffer.minCartValue && cartSubtotal < targetOffer.minCartValue) {
                return {
                    canApply: false,
                    reason: `Minimum cart value of $${targetOffer.minCartValue} required`,
                    minValue: targetOffer.minCartValue,
                    currentValue: cartSubtotal
                };
            }

            // Check categories if applicable
            if (targetOffer.applicableCategories && targetOffer.applicableCategories.length > 0) {
                const hasMatchingCategory = categories.some(category =>
                    targetOffer.applicableCategories.includes(category)
                );

                if (!hasMatchingCategory) {
                    return {
                        canApply: false,
                        reason: 'Offer does not apply to items in cart'
                    };
                }
            }

            // Check validity dates
            const now = new Date();
            const validFrom = new Date(targetOffer.validFrom);
            const validTill = new Date(targetOffer.validTill);

            if (now < validFrom) {
                return {
                    canApply: false,
                    reason: `Offer starts on ${validFrom.toLocaleDateString()}`
                };
            }

            if (now > validTill) {
                return {
                    canApply: false,
                    reason: 'Offer has expired'
                };
            }

            return {
                canApply: true,
                offer: targetOffer,
                discountValue: targetOffer.discountValue,
                discountType: targetOffer.discountType,
                maxDiscount: targetOffer.maxDiscount
            };
        } catch (error) {
            console.error('Failed to check offer applicability:', error);
            return {
                canApply: false,
                reason: 'Error checking offer'
            };
        }
    }
}

export default OfferService;